import React from 'react';
import { 
  Terminal, Cpu, Shield, Zap, Globe, Code, 
  ChevronRight, Users, Activity 
} from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 relative font-sans overflow-hidden">
      
      {/* --- BACKGROUND GRID --- */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(34,211,238,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none z-0"></div>
      
      {/* --- DECORATIVE GLOW --- */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#22d3ee]/5 blur-[100px] rounded-full pointer-events-none"></div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        
        {/* --- HERO SECTION --- */}
        <div className="max-w-4xl mx-auto text-center mb-24">
          <div className="inline-flex items-center gap-2 text-[#22d3ee] font-mono text-xs mb-4 border border-[#22d3ee]/30 px-3 py-1 rounded-full bg-[#22d3ee]/5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22d3ee] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22d3ee]"></span>
            </span>
            SYSTEM_STATUS: ONLINE
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white tracking-tight mb-6">
            Architecting the <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#22d3ee] to-blue-600">
              Digital Future
            </span>
          </h1>
          
          <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            We build high-performance, scalable, and aesthetically superior web templates for the next generation of developers and creators.
          </p>
        </div>

        {/* --- MISSION TERMINAL --- */}
        <div className="max-w-5xl mx-auto mb-24">
          <div className="bg-[#0f172a] border border-slate-700 rounded-lg overflow-hidden shadow-2xl relative group">
            {/* Terminal Header */}
            <div className="bg-[#1e293b] px-4 py-2 flex items-center gap-2 border-b border-slate-700">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="ml-2 font-mono text-xs text-slate-400">mission_protocol.exe</span>
            </div>
            
            {/* Terminal Body */}
            <div className="p-6 md:p-10 font-mono text-sm md:text-base">
              <p className="text-[#22d3ee] mb-2">$ init mission --verbose</p>
              <p className="text-slate-300 mb-4">
                {'>'} Initializing core values...<br/>
                {'>'} Loading "Quality_First" modules...<br/>
                {'>'} Establishing connection with "Innovation"...<br/>
              </p>
              <div className="pl-4 border-l-2 border-[#22d3ee]/30 mb-4">
                <p className="text-white italic">
                  "Our goal is to reduce development time by 70% while increasing visual fidelity. We provide the source code that powers the modern web, stripped of bloat and optimized for speed."
                </p>
              </div>
              <p className="text-[#22d3ee] animate-pulse">_</p>
            </div>

            {/* Decor Corner */}
            <div className="absolute bottom-0 right-0 p-4 opacity-10">
                <Terminal size={100} className="text-[#22d3ee]"/>
            </div>
          </div>
        </div>

        {/* --- CORE SPECS (Features) --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24 max-w-6xl mx-auto">
            <FeatureCard 
                icon={<Cpu />} 
                title="Optimized Core" 
                desc="Built on modern frameworks like React and Node.js ensuring maximum efficiency."
            />
            <FeatureCard 
                icon={<Shield />} 
                title="Secure Architecture" 
                desc="Enterprise-grade security standards implemented in every template module."
            />
            <FeatureCard 
                icon={<Zap />} 
                title="High Velocity" 
                desc="Lightweight code designed for instant load times and high SEO ranking."
            />
        </div>

        {/* --- STATS HUD --- */}
        <div className="border-y border-[#22d3ee]/20 bg-[#0f172a]/50 backdrop-blur-sm py-12 mb-24">
            <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <StatItem number="50+" label="Templates" />
                <StatItem number="1.2k" label="Downloads" />
                <StatItem number="99%" label="Uptime" />
                <StatItem number="24/7" label="Support" />
            </div>
        </div>

        {/* --- TEAM / FOUNDER SECTION --- */}
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-12">
            <div className="relative w-48 h-48 md:w-64 md:h-64 flex-shrink-0">
                {/* Tech Frame around image */}
                <div className="absolute inset-0 border-2 border-[#22d3ee] rounded-full opacity-20 animate-spin-slow"></div>
                <div className="absolute inset-2 border border-dashed border-white/20 rounded-full animate-reverse-spin"></div>
                
                {/* Image Placeholder */}
                <div className="absolute inset-4 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 overflow-hidden flex items-center justify-center border border-[#22d3ee]/50">
                    <Users size={64} className="text-slate-600" />
                    {/* <img src="/your-photo.jpg" className="w-full h-full object-cover" /> */}
                </div>
            </div>

            <div className="text-center md:text-left">
                <h2 className="text-3xl font-bold text-white mb-2">The <span className="text-[#22d3ee]">Operator</span></h2>
                <p className="text-xs font-mono text-slate-500 mb-4 uppercase tracking-widest">// Lead Developer & Architect</p>
                <p className="text-slate-400 mb-6 leading-relaxed">
                    Passionate about merging clean code with futuristic design. With over 5 years of full-stack experience, I created this platform to help developers bypass the repetitive setup phase and jump straight into building amazing products.
                </p>
                
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    <TechBadge text="React.js" />
                    <TechBadge text="Node" />
                    <TechBadge text="Three.js" />
                    <TechBadge text="Tailwind" />
                </div>
            </div>
        </div>

        {/* --- CTA --- */}
        <div className="mt-24 text-center">
            <h3 className="text-2xl text-white font-bold mb-6">Ready to upgrade your system?</h3>
            <Link 
                to="/products"
                className="inline-flex items-center gap-3 bg-[#22d3ee] text-[#020617] px-8 py-4 font-bold text-sm uppercase tracking-widest hover:bg-[#06b6d4] transition-all hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]"
            >
                <Code size={18} /> Access Library <ChevronRight size={18} />
            </Link>
        </div>

      </div>
    </div>
  );
};

// --- SUB-COMPONENTS ---

const FeatureCard = ({ icon, title, desc }) => (
    <div className="bg-[#0f172a] p-8 border border-slate-800 hover:border-[#22d3ee] transition-all duration-300 group">
        <div className="w-12 h-12 bg-[#22d3ee]/10 flex items-center justify-center text-[#22d3ee] mb-6 rounded-sm group-hover:scale-110 transition-transform">
            {icon}
        </div>
        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#22d3ee] transition-colors">{title}</h3>
        <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
    </div>
);

const StatItem = ({ number, label }) => (
    <div>
        <div className="text-4xl md:text-5xl font-bold text-white mb-2 font-mono tracking-tighter">{number}</div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#22d3ee]">{label}</div>
    </div>
);

const TechBadge = ({ text }) => (
    <span className="px-3 py-1 border border-slate-700 text-slate-400 text-xs font-mono rounded hover:border-[#22d3ee] hover:text-[#22d3ee] transition-colors cursor-default">
        {text}
    </span>
);

export default About;